import { centsToDollars, formatCentsToDollars } from '../money';
import { removeIllegalCharactersFromFileName } from '../documents';
import {
	GhostPracticeAccountingItem,
	NotApplicableValue,
} from 'src/app/private/sale/accounting/accounting.models';
import { jsPDFWithPlugin } from 'src/app/private/home/components/all-files-list/all-files-list.component';
import jsPDF from 'jspdf';
import JSZip from 'jszip';
import { StatementOfAccountTotals } from '@services/financialTotals/financial-totals.service';
import { formatPercent } from '../number';
import { GhostPracticeCode } from '@models/ghost-practice';
import { format } from 'date-fns';
import { RowInput, UserOptions } from 'jspdf-autotable';

interface jsPDFWithAutoTable extends jsPDFWithPlugin {
	lastAutoTable: { finalY: number };
}

export const getGhostPracticeFeeTxtBlob = (
	fees: GhostPracticeAccountingItem[],
	accountingNumber: string,
): Blob => {
	const lines = fees.map((item) =>
		[
			`"${accountingNumber}"`,
			`"${centsToDollars(item.amount)}"`,
			'""',
			`"${item.title}"`,
		].join(' '),
	);

	return new Blob([lines.join('\r\n')], { type: 'text/plain' });
};

export const getGhostPracticeDisbTxtBlob = (
	disbursements: GhostPracticeAccountingItem[],
	accountingNumber: string,
): Blob => {
	const lines = disbursements.map((item) =>
		[
			`"${accountingNumber}"`,
			'""',
			`"${item.ghostPracticeCode?.glAccount || ''}"`,
			`"${item.title}"`,
			`"${centsToDollars(item.amount)}"`,
			`"${item.ghostPracticeCode?.taxApplies || item.hst ? 'Y' : 'N'}"`,
		].join(' '),
	);

	return new Blob([lines.join('\r\n')], { type: 'text/plain' });
};

export const ghostPracticeExport = async (
	fees: GhostPracticeAccountingItem[],
	disbursements: GhostPracticeAccountingItem[],
	accountingNumber: string,
	fileNumber: string,
	fileName: string,
) => {
	// Sanitize file identifiers
	const sanitizedAccountingNumber =
		removeIllegalCharactersFromFileName(accountingNumber);
	const sanitizedFileName = removeIllegalCharactersFromFileName(fileName);

	const date = format(new Date(), 'MM-dd-yyyy');

	const zip = new JSZip();

	// Add fee file if there are fees
	if (fees.length > 0) {
		const feesBlob = getGhostPracticeFeeTxtBlob(fees, accountingNumber);
		zip.file(
			`Fees_${sanitizedAccountingNumber}_${sanitizedFileName}_${date}.txt`,
			feesBlob,
		);
	}

	// Add disbursement file if there are disbursements
	if (disbursements.length > 0) {
		const disbursementsBlob = getGhostPracticeDisbTxtBlob(
			disbursements,
			accountingNumber,
		);
		zip.file(
			`Disbursements_${sanitizedAccountingNumber}_${sanitizedFileName}_${date}.txt`,
			disbursementsBlob,
		);
	}

	// Generate and download the zip file
	const a = document.createElement('a');
	const url = window.URL.createObjectURL(
		await zip.generateAsync({ type: 'blob' }),
	);

	a.href = url;
	a.download = `GhostPractice_${sanitizedAccountingNumber}_${sanitizedFileName}_${date}.zip`;
	a.click();
	window.URL.revokeObjectURL(url);
	a.remove();
};

/**
 * Generates a printable PDF for GhostPractice accounting export
 */
export const ghostPracticePrint = (
	hstItems: GhostPracticeAccountingItem[],
	nonHstItems: GhostPracticeAccountingItem[],
	conveyancingFeesItems: GhostPracticeAccountingItem[],
	statementOfAccountTotals: StatementOfAccountTotals,
	hst: number,
	accountingNumber: string,
	fileNumber: string,
	fileName: string,
) => {
	const doc = new jsPDF({
		orientation: 'portrait',
		unit: 'mm',
	}) as jsPDFWithAutoTable;

	doc.setFontSize(10);
	doc.setFont(undefined, 'bold');
	doc.text(`Date: ${new Date().toLocaleDateString('en-US')}`, 14, 10);
	doc.text(`File Number: ${fileNumber}`, 14, 14);
	doc.text(`File Name: ${fileName}`, 14, 18);
	doc.text(`Accounting Number: ${accountingNumber}`, 14, 22);

	const columnStyles = {
		0: { cellWidth: 80 },
		1: { cellWidth: 30 },
		2: { cellWidth: 80, halign: 'right' },
	};

	const headStyles = {
		fillColor: '044321',
	};

	const {
		feesHST,
		feesTotalWithHST,
		HSTDisbursementsHST,
		HSTDisbursementsTotal,
		nonHSTDisbursementsTotal,
	} = statementOfAccountTotals;

	// Conveyancing Fees Table
	const conveyancingFeesBodyItems = [
		...conveyancingFeesItems.map((item) => [
			item.title,
			`${formatCentsToDollars(item.amount)}`,
			`${getGhostPracticeCodeLabelDescription(item?.ghostPracticeCode)}`,
		]),
		[
			'\n Total',
			' ',
			`HST (${formatPercent(hst)}%) ${formatCentsToDollars(feesHST)}\n${formatCentsToDollars(feesTotalWithHST)}`,
		],
	];

	const conveyancingFeesHeaders = [
		`Legal Fees (${conveyancingFeesItems.length})`,
		'Amount',
		'G/L Account',
	];

	doc.autoTable({
		startY: 26,
		head: [conveyancingFeesHeaders] as RowInput[],
		body: conveyancingFeesBodyItems,
		columnStyles: columnStyles as UserOptions['columnStyles'],
		headStyles: headStyles as UserOptions['headStyles'],
		didParseCell: function (data) {
			const { cell, column } = data;
			if (
				conveyancingFeesHeaders.includes(cell.raw as string) &&
				column.index === conveyancingFeesHeaders.length - 1
			) {
				cell.styles.halign = 'right';
			}

			if (data.row.index === conveyancingFeesBodyItems.length - 1) {
				data.cell.styles.fontStyle = 'bold';
			}
		},
	});

	// HST Disbursements Table
	const hstDisbursementsBodyItems = [
		...hstItems.map((item) => [
			item.title,
			`${formatCentsToDollars(item.amount)}`,
			`${getGhostPracticeCodeLabelDescription(item?.ghostPracticeCode)}`,
		]),
		[
			'\n Total',
			' ',
			`HST (${formatPercent(hst)}%) ${formatCentsToDollars(HSTDisbursementsHST)}\n${formatCentsToDollars(HSTDisbursementsTotal + HSTDisbursementsHST)}`,
		],
	];

	const hstDisbursementsHeaders = [
		`HST Disbursements (${hstItems.length})`,
		'Amount',
		'G/L Account',
	];

	doc.autoTable({
		startY: doc.lastAutoTable.finalY + 10,
		head: [hstDisbursementsHeaders] as RowInput[],
		body: hstDisbursementsBodyItems,
		columnStyles: columnStyles as UserOptions['columnStyles'],
		headStyles: headStyles as UserOptions['headStyles'],
		didParseCell: function (data) {
			const { cell, column } = data;
			if (
				hstDisbursementsHeaders.includes(cell.raw as string) &&
				column.index === conveyancingFeesHeaders.length - 1
			) {
				cell.styles.halign = 'right';
			}

			if (data.row.index === hstDisbursementsBodyItems.length - 1) {
				data.cell.styles.fontStyle = 'bold';
			}
		},
	});

	// Non-HST Disbursements Table
	const nonHstDisbursementsBodyItems = [
		...nonHstItems.map((item) => [
			item.title,
			`${formatCentsToDollars(item.amount)}`,
			`${getGhostPracticeCodeLabelDescription(item?.ghostPracticeCode)}`,
		]),
		['Total', '', `${formatCentsToDollars(nonHSTDisbursementsTotal)}`],
	];

	const nonHstDisbursementsHeaders = [
		`Non HST Disbursements (${nonHstItems.length})`,
		'Amount',
		'G/L Account',
	];

	doc.autoTable({
		startY: doc.lastAutoTable.finalY + 10,
		head: [nonHstDisbursementsHeaders] as RowInput[],
		body: nonHstDisbursementsBodyItems,
		columnStyles: columnStyles as UserOptions['columnStyles'],
		headStyles: headStyles as UserOptions['headStyles'],
		didParseCell: function (data) {
			const { cell, column } = data;
			if (
				nonHstDisbursementsHeaders.includes(cell.raw as string) &&
				column.index === conveyancingFeesHeaders.length - 1
			) {
				cell.styles.halign = 'right';
			}

			if (data.row.index === nonHstDisbursementsBodyItems.length - 1) {
				data.cell.styles.fontStyle = 'bold';
			}
		},
	});

	doc.autoPrint();

	const url = doc.output('bloburl').toString();

	return url;
};

/**
 * Helper function to get label description for GhostPractice code dropdown
 */
export const getGhostPracticeCodeLabelDescription = (
	code: GhostPracticeCode,
) => {
	if (!code || code.id === NotApplicableValue) {
		return NotApplicableValue;
	}

	return `${code.glAccount} - ${code.description}`;
};
